import { supabase } from '@/integrations/supabase/client';
import { CaseStudyItem } from '@/types/case-study';

/**
 * CaseStudyService for wcd-website
 * Read-only service for fetching case studies from Supabase
 */
export class CaseStudyService {
  
  /**
   * Get a single case study by ID
   */
  static async getCaseStudy(id: string): Promise<CaseStudyItem> {
    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return this.mapToCaseStudyItem(caseStudy);
  }

  /**
   * Get a single case study by slug
   */
  static async getCaseStudyBySlug(slug: string): Promise<CaseStudyItem> {
    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .select(`
        *,
        profiles:profile_id (
          username,
          display_name,
          avatar_url,
          username_slug
        )
      `)
      .eq('slug', slug)
      .eq('status', 'published') // Only get published case studies
      .single();

    if (error) throw error;
    return this.mapToCaseStudyItem(caseStudy);
  }

  /**
   * List case studies with optional filters
   * By default, only returns published case studies for public consumption
   */
  static async listCaseStudies(profileId?: string, status?: 'draft' | 'published'): Promise<CaseStudyItem[]> {
    let query = supabase
      .from('case_studies')
      .select('*')
      .eq('status', 'published') // Only published case studies by default
      .order('order_index', { ascending: true });

    if (profileId) {
      query = query.eq('profile_id', profileId);
    }

    // Override status filter if explicitly provided
    if (status) {
      query = query.eq('status', status);
    }

    const { data: caseStudies, error } = await query;

    if (error) throw error;
    return (caseStudies || []).map(this.mapToCaseStudyItem);
  }

  /**
   * Map database row to CaseStudyItem type
   */
  private static mapToCaseStudyItem(data: any): CaseStudyItem {
    return {
      id: data.id,
      title: data.title,
      description: data.description || '',
      cover_image: data.cover_image || '',
      sections: Array.isArray(data.sections) ? data.sections : [],
      created_at: data.created_at,
      updated_at: data.updated_at,
      is_featured: data.is_featured || false,
      status: data.status || 'draft',
      order: data.order_index || 0,
      slug: data.slug,
      seo_title: data.seo_title,
      seo_description: data.seo_description,
      og_image: data.og_image,
      profile_id: data.profile_id,
      profile: data.profiles ? {
        username: data.profiles.username,
        display_name: data.profiles.display_name,
        avatar_url: data.profiles.avatar_url,
        username_slug: data.profiles.username_slug,
      } : undefined,
    };
  }
}
